import {blogService} from '../services'
import {deepCopyReactive, getBlogDetails, hasObjectChanged} from "../helpers/index.js";

const state = {
    all: {
        loading: false,
        total: 0,
        rows: []
    },
    blog: {
        urls: {},
        settings: {
            plausible: {},
            recommendations: {},
            stripe: {},
            ghost: {
                tiers: [],
                authors: [],
            },
            gifts: {},
            webhooks: {},
            donations: {},
            mailgun: {},
            plansSubscriptions: {},
            autoresponder: {
                excluded: {
                    'tiers': [],
                    'prices': [],
                    'products': [],
                }
            },
            cta: {
                excludeRefs: null,
                excludeTags: null,
            },
            advanced_autoresponder_on: false,
        },
        subscription_plan: 'basic',
        use_portal_links: true,
        portal_links: {
            'membership': null,
            'signin': null,
            'signup': null,
            'upgrade': null,
            'account': null,
        },
    },
    blogOriginal: {}, // we are using this for identify changes in blog settings
    request: {
        loading: false
    },
    subscriptionRequest: {
        loading: false
    },
    blogIntegrations: {},
    integratiuonRequest: {
        loading: false
    },
    blogMetadata: {
        tiers: [],
        labels: [],
        tags: [],
        templates: [],
    },
    blogUsers: {
        loading: false,
        rows: [],
    },
    blogInvites: {
        loading: false,
        rows: [],
    },
    demoPosts: [],
}

const SENSITIVE_FIELD_MARKER = '******************'
const SENSITIVE_FIELDS = {
    stripe: [
        'publicKey',
        'secretKey'
    ],
    ghost: [
        'contentKey',
        'apiKey',
    ],
    mailgun: [
        'secret',
    ],
    donations: [
        'clientId',
        'secret',
    ],
    youtube: [
        'apiKey',
    ],
};

const actions = {

    get ({ dispatch, commit }, id) {
        commit('getBlogRequest')
        blogService.get(id).then(
            response => {
                commit('getBlogSuccess', response)
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },
    getNew ({ dispatch, commit }) {
        commit('getBlogRequest')
        return blogService.getNew().then(
            response => {
                commit('getBlogSuccess', response)
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },
    getWithSubscription ({ dispatch, commit }, id) {
        commit('getBlogRequest')
        return blogService.getWithSubscription(id).then(
            response => {
                commit('getBlogSuccess', response)
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },
    cancelSubscription ({ dispatch, commit }, id) {
        commit('getBlogSubscriptionRequest')
        return blogService.cancelSubscription(id).then(
            response => {
                commit('getBlogSubscriptionSuccess', response)
            }
        )
    },
    resumeSubscription ({ dispatch, commit }, id) {
        commit('getBlogSubscriptionRequest')
        return blogService.resumeSubscription(id).then(
            response => {
                commit('getBlogSubscriptionSuccess', response)
            }
        )
    },

    getProtected ({ dispatch, commit }, id) {
        commit('getBlogRequest')
        return blogService.get(id).then(
            response => {
                commit('getBlogSuccessWithProtect', response)
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },

    saveSettings ({ dispatch, commit }, params) {
        commit('getBlogRequest')
        return blogService.saveSettings(params).then(
            response => {
                commit('getBlogSuccessWithProtect', response)
                dispatch('alert/success', 'Settings have been updated successfully.', { root: true })
                return response
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', 'Unable to save settings: ' + error, { root: true })
            }
        )
    },

    saveTipJarSettings ({ dispatch, commit }, params) {
        commit('getBlogRequest')
        blogService.saveSettings(params).then(
            response => {
                commit('getBlogSuccessWithProtect', response)
                dispatch('alert/success', 'Tip Jar settings have been updated successfully.', { root: true })
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', 'Unable to save Tip Jar settings: ' + error, { root: true })
            }
        )
    },

    saveBlogSettings ({ dispatch, commit }, params) {
        commit('getBlogRequest')
        return blogService.saveBlogSettings(params).then(
            response => {
                commit('getBlogSuccess', response)
                dispatch('alert/success', 'Site settings have been updated successfully.', { root: true })
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', 'Unable to save settings: ' + error, { root: true })
            }
        )
    },

    loadUsers ({ dispatch, commit }) {
        commit('getBlogUsersRequest')
        blogService.getUsers().then(
            response => {
                commit('getBlogUsersSuccess', response)
            },
            error => {
                commit('getBlogUsersError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },

    loadInvites ({ dispatch, commit }) {
        commit('getBlogInvitesRequest')
        blogService.getInvites().then(
            response => {
                commit('getBlogInvitesSuccess', response)
            },
            error => {
                commit('getBlogInvitesError')
                dispatch('alert/error', error, { root: true })
            }
        )
    },

    updateUser ({ dispatch, commit }, { id, params }) {
        return blogService.updateUser(id, params).then(
            response => {
                dispatch('alert/success', 'User is updated successfully.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to update user: ' + error, { root: true })
            }
        )
    },

    deleteUser ({ dispatch, commit }, id) {
        return blogService.deleteUser(id).then(
            response => {
                dispatch('alert/success', 'User removed successfully.', { root: true })
                dispatch('loadUsers')
            },
            error => {
                dispatch('alert/error', 'Unable to remove user: ' + error, { root: true })
            }
        )
    },

    inviteUser ({ dispatch, commit }, params) {
        blogService.inviteUser(params).then(
            response => {
                dispatch('alert/successPermanent', 'Invitation URL: ' +
                    response.invitation_url + '. Sent to user email address.', { root: true })

                setTimeout(function () {
                    dispatch('loadInvites')
                }, 2000)
            },
            error => {
                dispatch('alert/error', 'Unable to invite user: ' + error, { root: true })
            }
        )
    },

    resendInvite ({ dispatch, commit }, id) {
        blogService.resendInvite(id).then(
            response => {
                dispatch('alert/success', 'Invitation URL: ' +
                    response.invitation_url + '. Sent to user email address.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to resend invite: ' + error, { root: true })
            }
        )
    },

    deleteInvite ({ dispatch, commit }, id) {
        blogService.deleteInvite(id).then(
            response => {
                dispatch('alert/success', 'Invitation has been removed successfully.', { root: true })
                dispatch('loadInvites')
            },
            error => {
                dispatch('alert/error', 'Unable to delete invite: ' + error, { root: true })
            }
        )
    },

    removeLogo({ dispatch, commit }, id) {
        blogService.removeLogo(id).then(
            response => {
                dispatch('get', id)
            },
            error => {
                dispatch('alert/error', 'Unable to update blog settings: ' + error, { root: true })
            }
        )
    },

    refreshGhostSettings({ dispatch, commit }, id) {
        blogService.refreshGhostSettings(id).then(
            response => {
                dispatch('alert/success', 'Settings have been refreshed successfully.', { root: true })
                dispatch('get', id)
            },
            error => {
                dispatch('alert/error', 'Unable to refresh blog settings: ' + error, { root: true })
            }
        )
    },

    loadIntegrationData({ dispatch, commit }, params) {
        const integrationName = params.name
        const settings = params.settings

        commit('startRequest')
        return blogService.getIntegrationData(integrationName, settings).then(
            response => {
                commit('endRequestSuccess')
                commit('getBlogIntegrationDataSuccess', { integrationName, response })
                return response
            },
            error => {
                dispatch('alert/error', 'Unable to get data for '+ integrationName + ' integration: ' + error, { root: true })
                commit('endRequestError')
            }
        )
    },

    requestPlanUpgrade ({ dispatch }) {
        blogService.requestPlanUpgrade().then(
            response => {
                dispatch('alert/success', 'Thank you, we will respond you shortly.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to finish request: ' + error, { root: true })
            }
        )
    },

    loadMetadata ({ dispatch, commit }) {
        commit('getBlogMetadataRequest')
        return blogService.loadMetadata().then(
            response => {
                commit('getBlogMetadataSuccess', response)
                return response
            },
            error => {
                commit('getBlogMetadataError')
                dispatch('alert/error', 'Unable to load blog metadata: ' + error, { root: true })
            }
        )
    },
    saveNotifications ({ dispatch, commit }, params) {
        commit('getBlogRequest')
        blogService.saveNotifications(params).then(
            response => {
                commit('getBlogSuccess', response)
                dispatch('alert/success', 'Notifications have been updated successfully.', { root: true })
            },
            error => {
                commit('getBlogError')
                dispatch('alert/error', 'Unable to save notifications: ' + error, { root: true })
            }
        )
    },

    requestDeleteAccount ({ dispatch }) {
        blogService.requestDeleteAccount().then(
            response => {
                dispatch('alert/success', 'Your request was received. We will process this and get back to you as soon as possible, generally within one business day.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to finish request: ' + error, { root: true })
            }
        )
    },

    getCssColors ({ dispatch, commit }) {
        return blogService.getCssColors().then(
            response => {
                commit('getBlogSuccess', response)
            },
            error => {
                dispatch('alert/error', 'Unable to process blog CSS colors: ' + error, { root: true })
            }
        )
    },

    loadDemoPosts ({ dispatch, commit }) {
        commit('getBlogRequest')
        blogService.loadDemoPosts().then(
            response => {
                commit('loadDemoPostsSuccess', response)
            },
            error => {
                dispatch('alert/error', 'Unable to load demo posts: ' + error, { root: true })
                commit('loadDemoPostsError')
            }
        )
    },

    createDemoPosts ({ dispatch, commit }) {
        commit('getBlogRequest')
        blogService.createDemoPosts().then(
            response => {
                commit('loadDemoPostsSuccess', response)
                dispatch('alert/success', 'Demo Posts successfully.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to create demo posts: ' + error, { root: true })
                commit('loadDemoPostsError')
            }
        )
    },

    deleteDemoPosts ({ dispatch, commit }) {
        commit('getBlogRequest')
        blogService.deleteDemoPosts().then(
            response => {
                commit('loadDemoPostsSuccess', response)
                dispatch('alert/success', 'Demo Posts deleted successfully.', { root: true })
            },
            error => {
                dispatch('alert/error', 'Unable to create demo posts: ' + error, { root: true })
                commit('loadDemoPostsError')
            }
        )
    },
}

const mutations = {
    getBlogRequest (state) {
        state.request.loading = true
    },
    getBlogSuccess (state, data) {
        state.blog = data.blog
        state.request.loading = false
        state.blogOriginal = deepCopyReactive(data.blog)
    },
    getBlogSubscriptionRequest (state) {
        state.subscriptionRequest.loading = true
    },
    getBlogSubscriptionSuccess (state, data) {
        state.blog = data.blog
        state.subscriptionRequest.loading = false
        state.blogOriginal = deepCopyReactive(data.blog)
    },
    getBlogError (state) {
        state.request.loading = false
    },
    getBlogSuccessWithProtect (state, data) {
        state.request.loading = false

        let settings = data.blog.settings;

        if (settings) {
            for (const [settingGroup, settingGroupKeys] of Object.entries(SENSITIVE_FIELDS)) {
                if (settings[settingGroup]) {
                    for (const [settingIdx, settingValue] of Object.entries(settingGroupKeys)) {
                        if (settings[settingGroup][settingValue]) {
                            let fieldValue = settings[settingGroup][settingValue];
                            let newFieldValue = fieldValue.substr(0, 15) + SENSITIVE_FIELD_MARKER +
                                fieldValue.substr(fieldValue.length - 10);
                            settings[settingGroup][settingValue] = newFieldValue;
                        }
                    }
                }
            }
            data.blog.settings = settings
        }

        state.blog = data.blog
        state.blogOriginal = deepCopyReactive(data.blog)
    },
    getBlogIntegrationDataSuccess(state, { integrationName, response }) {
        state.blogIntegrations = {
            ...state.blogIntegrations,
            [ integrationName ]: response.data
        }
    },
    startRequest(state) {
        state.request.loading = true
    },
    endRequestSuccess(state) {
        state.request.loading = false
    },
    endRequestError(state) {
        state.request.loading = false
    },
    getBlogMetadataRequest(state) {
        state.blogMetadata = {loading: true}
    },
    getBlogMetadataSuccess(state, response) {
        state.blogMetadata = {
            loading: false,
            tiers: response.tiers,
            labels: response.labels,
            actions: response.actions,
            templates: response.templates,
            tags: response.tags,
        }
    },
    getBlogMetadataError(state, response) {
        state.blogMetadata = {loading: false}
    },
    getBlogUsersRequest(state) {
        state.blogUsers.loading = true
    },
    getBlogUsersSuccess(state, data) {
        state.blogUsers = {loading: false, rows: data.users}
    },
    getBlogUsersError(state) {
        state.blogUsers.loading = false
    },
    getBlogInvitesRequest(state) {
        state.blogInvites.loading = true
    },
    getBlogInvitesSuccess(state, data) {
        state.blogInvites = {loading: false, rows: data.invites}
    },
    getBlogInvitesError(state) {
        state.blogInvites.loading = false
    },
    loadDemoPostsSuccess(state, data) {
        state.request.loading = false
        state.demoPosts = data.posts
    },
    loadDemoPostsError(state) {
        state.request.loading = false
        state.demoPosts = []
    }
}

const getters = {
    isLoadingBlog (state) {
        return state.request.loading === true
    },
    isLoading (state) {
        return state.request.loading === true
    },
    isSubscriptionRequest (state) {
        return state.subscriptionRequest.loading === true
    },
    getBlog (state) {
        return state.blog
    },
    isBlogStripeConnected (state) {
        return state.blog.stripe_account_id ||
            (state.blog.settings && state.blog.settings.stripe.publicKey && state.blog.settings.stripe.secretKey)
    },
    isBlogPayPalConnected (state) {
        return state.blog.settings.donations.paypalConnected ||
            (state.blog.settings.donations.clientId && state.blog.settings.donations.secret)
    },
    isBlogPayPalLinkSet (state) {
        return state.blog.settings && state.blog.settings.donations.paypalUrl
    },
    getTransistorFmShowsOptions(state) {
        let options = []
        if (state.blogIntegrations.transistorFm && state.blogIntegrations.transistorFm.shows) {
            for (const [idx, row] of Object.entries(state.blogIntegrations.transistorFm.shows)) {
                options.push({
                    'value': row.id,
                    'text': row.attributes.title,
                })
            }
        }
        return options
    },
    getWebflowSitesOptions(state) {
        let options = []
        if (state.blogIntegrations.webflow && state.blogIntegrations.webflow.sites) {
            for (const [idx, row] of Object.entries(state.blogIntegrations.webflow.sites)) {
                options.push({
                    'value': row.id,
                    'text': row.displayName,
                })
            }
        }
        return options
    },
    getMegaphonePodcastsOptions(state) {
        let options = []
        if (state.blogIntegrations.megaphone && state.blogIntegrations.megaphone.podcasts) {
            for (const [idx, row] of Object.entries(state.blogIntegrations.megaphone.podcasts)) {
                options.push({
                    'value': row.id,
                    'text': row.title,
                })
            }
        }
        return options
    },
    isIntegrationRequestLoading (state) {
        return state.integratiuonRequest.loading === true
    },
    getMetadata(state) {
        return state.blogMetadata
    },
    getMetadataTiers(state) {
        return state.blogMetadata.tiers
    },
    getMetadataLabels(state) {
        return state.blogMetadata.labels
    },
    getMetadataTags(state) {
        return state.blogMetadata.tags
    },
    getMetadataTemplates(state) {
        return state.blogMetadata.templates
    },
    isGiftNotSpecified(state, getters) {
        if (!getters.isLoadingBlog) {
            if (getters.isBlogSubscriptionAdvanced) {
                if (!state.blog.settings.plansSubscriptions || !state.blog.settings.plansSubscriptions.gift) {
                    return true;
                }
            }
        }
        return false
    },
    getGiftNotSpecifiedMessage() {
        return MESSAGE_GIFT_NOT_SPECIFIED
    },
    getSubscriptionPlan(state) {
        const blogDto = getBlogDetails();

        if (blogDto && blogDto.subscription_plan) {
            return blogDto.subscription_plan
        } else if (state.blog.subscription_plan) {
            return state.blog.subscription_plan
        }
        return null
    },
    isBlogSubscriptionBasic(state, getters) {
        return getters.getSubscriptionPlan === 'basic';
    },
    isBlogSubscriptionAdvanced(state, getters) {
        return getters.getSubscriptionPlan !== 'basic';
    },
    isBlogSubscriptionMiddle(state, getters) {
        return getters.getSubscriptionPlan === 'middle';
    },
    getTiersForSelect(state, getters) {
        let tiers = [
            {value: null, text: 'Select Tier'},
        ]

        if (getters.getMetadataTiers) {
            for (const [idx, tier] of Object.entries(getters.getMetadataTiers)) {
                tiers.push({value: tier.id, text: tier.name});
            }
        }
        return tiers
    },
    getTiers(state, getters) {
        let tiers = []

        if (getters.getMetadataTiers) {
            for (const [idx, tier] of Object.entries(getters.getMetadataTiers)) {
                tiers.push({value: tier.id, text: tier.name});
            }
        }
        return tiers
    },
    getBlogUsers(state) {
        return state.blogUsers.rows
    },
    getBlogInvites(state) {
        return state.blogInvites.rows
    },
    getDemoPosts(state) {
        return state.demoPosts
    },
    getTagsForSelect(state, getters) {
        if (getters.getMetadataTags) {
            return getters.getMetadataTags
                .map(f => { return { text: f.name, value: f.name } })
        }
        return []
    },
    isPlausibleConnected(state, getters) {
        return getters.getBlog.settings.plausible && getters.getBlog.settings.plausible.connected;
    },
    isBlogChanged(state, getters) {
        return hasObjectChanged(state.blog, state.blogOriginal) || hasObjectChanged(state.blogOriginal, state.blog);
    }
}

const MESSAGE_BASIC_PLAN_RESTRICTION = "Upgrade to the <b>Luna</b> plan to <a class='btn btn-primary btn-sm' href='/home#/request-upgrade'>Enable</a> this feature"
const MESSAGE_GIFT_NOT_SPECIFIED = "Warning: Gift Subscriptions can't be offered until you assign a price. Please go to <a href='/home/#gift-subscriptions-settings'>Gift Subscriptions</a> and select a price."

const TIME_ZONES = [
    {value:"Pacific/Midway",text:"(GMT-11:00) Midway Island, Samoa"},
    {value:"America/Adak",text:"(GMT-10:00) Hawaii-Aleutian"},
    {value:"Etc/GMT+10",text:"(GMT-10:00) Hawaii"},
    {value:"Pacific/Marquesas",text:"(GMT-09:30) Marquesas Islands"},
    {value:"Pacific/Gambier",text:"(GMT-09:00) Gambier Islands"},
    {value:"America/Anchorage",text:"(GMT-09:00) Alaska"},
    {value:"America/Ensenada",text:"(GMT-08:00) Tijuana, Baja California"},
    {value:"Etc/GMT+8",text:"(GMT-08:00) Pitcairn Islands"},
    {value:"America/Los_Angeles",text:"(GMT-08:00) Pacific Time (US & Canada)"},
    {value:"America/Denver",text:"(GMT-07:00) Mountain Time (US & Canada)"},
    {value:"America/Chihuahua",text:"(GMT-07:00) Chihuahua, La Paz, Mazatlan"},
    {value:"America/Dawson_Creek",text:"(GMT-07:00) Arizona"},
    {value:"America/Belize",text:"(GMT-06:00) Saskatchewan, Central America"},
    {value:"America/Cancun",text:"(GMT-06:00) Guadalajara, Mexico City, Monterrey"},
    {value:"Chile/EasterIsland",text:"(GMT-06:00) Easter Island"},
    {value:"America/Chicago",text:"(GMT-06:00) Central Time (US & Canada)"},
    {value:"America/New_York",text:"(GMT-05:00) Eastern Time (US & Canada)"},
    {value:"America/Havana",text:"(GMT-05:00) Cuba"},
    {value:"America/Bogota",text:"(GMT-05:00) Bogota, Lima, Quito, Rio Branco"},
    {value:"America/Caracas",text:"(GMT-04:30) Caracas"},
    {value:"America/Santiago",text:"(GMT-04:00) Santiago"},
    {value:"America/La_Paz",text:"(GMT-04:00) La Paz"},
    {value:"Atlantic/Stanley",text:"(GMT-04:00) Faukland Islands"},
    {value:"America/Campo_Grande",text:"(GMT-04:00) Brazil"},
    {value:"America/Goose_Bay",text:"(GMT-04:00) Atlantic Time (Goose Bay)"},
    {value:"America/Glace_Bay",text:"(GMT-04:00) Atlantic Time (Canada)"},
    {value:"America/St_Johns",text:"(GMT-03:30) Newfoundland"},
    {value:"America/Araguaina",text:"(GMT-03:00) UTC-3"},
    {value:"America/Montevideo",text:"(GMT-03:00) Montevideo"},
    {value:"America/Miquelon",text:"(GMT-03:00) Miquelon, St. Pierre"},
    {value:"America/Godthab",text:"(GMT-03:00) Greenland"},
    {value:"America/Argentina/Buenos_Aires",text:"(GMT-03:00) Buenos Aires"},
    {value:"America/Sao_Paulo",text:"(GMT-03:00) Brasilia"},
    {value:"America/Noronha",text:"(GMT-02:00) Mid-Atlantic"},
    {value:"Atlantic/Cape_Verde",text:"(GMT-01:00) Cape Verde Is."},
    {value:"Atlantic/Azores",text:"(GMT-01:00) Azores"},
    {value:"Europe/Belfast",text:"(GMT) Greenwich Mean Time : Belfast"},
    {value:"Europe/Dublin",text:"(GMT) Greenwich Mean Time : Dublin"},
    {value:"Europe/Lisbon",text:"(GMT) Greenwich Mean Time : Lisbon"},
    {value:"Europe/London",text:"(GMT) Greenwich Mean Time : London"},
    {value:"Etc/UTC",text:"Etc/UTC"},
    {value:"Africa/Abidjan",text:"(GMT) Monrovia, Reykjavik"},
    {value:"Europe/Amsterdam",text:"(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna"},
    {value:"Europe/Belgrade",text:"(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague"},
    {value:"Europe/Brussels",text:"(GMT+01:00) Brussels, Copenhagen, Madrid, Paris"},
    {value:"Africa/Algiers",text:"(GMT+01:00) West Central Africa"},
    {value:"Africa/Windhoek",text:"(GMT+01:00) Windhoek"},
    {value:"Asia/Beirut",text:"(GMT+02:00) Beirut"},
    {value:"Africa/Cairo",text:"(GMT+02:00) Cairo"},
    {value:"Asia/Gaza",text:"(GMT+02:00) Gaza"},
    {value:"Africa/Blantyre",text:"(GMT+02:00) Harare, Pretoria"},
    {value:"Asia/Jerusalem",text:"(GMT+02:00) Jerusalem"},
    {value:"Europe/Minsk",text:"(GMT+02:00) Minsk"},
    {value:"Europe/Kyiv",text:"(GMT+02:00) Kyiv"},
    {value:"Asia/Damascus",text:"(GMT+02:00) Syria"},
    {value:"Europe/Moscow",text:"(GMT+03:00) Moscow, St. Petersburg, Volgograd"},
    {value:"Africa/Addis_Ababa",text:"(GMT+03:00) Nairobi"},
    {value:"Asia/Tehran",text:"(GMT+03:30) Tehran"},
    {value:"Asia/Dubai",text:"(GMT+04:00) Abu Dhabi, Muscat"},
    {value:"Asia/Yerevan",text:"(GMT+04:00) Yerevan"},
    {value:"Asia/Kabul",text:"(GMT+04:30) Kabul"},
    {value:"Asia/Yekaterinburg",text:"(GMT+05:00) Ekaterinburg"},
    {value:"Asia/Tashkent",text:"(GMT+05:00) Tashkent"},
    {value:"Asia/Kolkata",text:"(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi"},
    {value:"Asia/Katmandu",text:"(GMT+05:45) Kathmandu"},
    {value:"Asia/Dhaka",text:"(GMT+06:00) Astana, Dhaka"},
    {value:"Asia/Novosibirsk",text:"(GMT+06:00) Novosibirsk"},
    {value:"Asia/Rangoon",text:"(GMT+06:30) Yangon (Rangoon)"},
    {value:"Asia/Bangkok",text:"(GMT+07:00) Bangkok, Hanoi, Jakarta"},
    {value:"Asia/Krasnoyarsk",text:"(GMT+07:00) Krasnoyarsk"},
    {value:"Asia/Hong_Kong",text:"(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi"},
    {value:"Asia/Irkutsk",text:"(GMT+08:00) Irkutsk, Ulaan Bataar"},
    {value:"Australia/Perth",text:"(GMT+08:00) Perth"},
    {value:"Australia/Eucla",text:"(GMT+08:45) Eucla"},
    {value:"Asia/Tokyo",text:"(GMT+09:00) Osaka, Sapporo, Tokyo"},
    {value:"Asia/Seoul",text:"(GMT+09:00) Seoul"},
    {value:"Asia/Yakutsk",text:"(GMT+09:00) Yakutsk"},
    {value:"Australia/Adelaide",text:"(GMT+09:30) Adelaide"},
    {value:"Australia/Darwin",text:"(GMT+09:30) Darwin"},
    {value:"Australia/Brisbane",text:"(GMT+10:00) Brisbane"},
    {value:"Australia/Hobart",text:"(GMT+10:00) Hobart"},
    {value:"Asia/Vladivostok",text:"(GMT+10:00) Vladivostok"},
    {value:"Australia/Lord_Howe",text:"(GMT+10:30) Lord Howe Island"},
    {value:"Etc/GMT-11",text:"(GMT+11:00) Solomon Is., New Caledonia"},
    {value:"Asia/Magadan",text:"(GMT+11:00) Magadan"},
    {value:"Australia/Sydney",text:"(GMT+11:00) Australia/Sydney"},
    {value:"Pacific/Norfolk",text:"(GMT+11:30) Norfolk Island"},
    {value:"Asia/Anadyr",text:"(GMT+12:00) Anadyr, Kamchatka"},
    {value:"Pacific/Auckland",text:"(GMT+12:00) Auckland, Wellington"},
    {value:"Etc/GMT-12",text:"(GMT+12:00) Fiji, Kamchatka, Marshall Is."},
    {value:"Pacific/Chatham",text:"(GMT+12:45) Chatham Islands"},
    {value:"Pacific/Tongatapu",text:"(GMT+13:00) Nuku'alofa"},
    {value:"Pacific/Kiritimati",text:"(GMT+14:00) Kiritimati"},
]

const CURRENCIES = [
    {"text":"USD - US Dollar","value":"USD"},
    {"text":"EUR - Euro","value":"EUR"},
    {"text":"GBP - British Pound Sterling","value":"GBP"},
    {"text":"AUD - Australian Dollar","value":"AUD"},
    {"text":"CAD - Canadian Dollar","value":"CAD"},
];

const FONTS = [
    {value: null, text: 'Site Default Font'},
    {value: 'Abel', text: 'Abel'},
    {value: 'Anton', text: 'Anton'},
    {value: 'Arial', text: 'Arial'},
    {value: 'Archivo', text: 'Archivo'},
    {value: 'Arimo', text: 'Arimo'},
    {value: 'Arvo', text: 'Arvo'},
    {value: 'Assistant', text: 'Assistant'},
    {value: 'Barlow', text: 'Barlow'},
    {value: 'Barlow Condensed', text: 'Barlow Condensed'},
    {value: 'Bebas Neue', text: 'Bebas Neue'},
    {value: 'Bitter', text: 'Bitter'},
    {value: 'Cabin', text: 'Cabin'},
    {value: 'Cairo', text: 'Cairo'},
    {value: 'Caveat', text: 'Caveat'},
    {value: 'Chakra Petch', text: 'Chakra Petch'},
    {value: 'Comfortaa', text: 'Comfortaa'},
    {value: 'Crimson Text', text: 'Crimson Text'},
    {value: 'Dancing Script', text: 'Dancing Script'},
    {value: 'DM Sans', text: 'DM Sans'},
    {value: 'Dosis', text: 'Dosis'},
    {value: 'EB Garamond', text: 'EB Garamond'},
    {value: 'Exo 2', text: 'Exo 2'},
    {value: 'Figtree', text: 'Figtree'},
    {value: 'Fira Sans', text: 'Fira Sans'},
    {value: 'Fjalla One', text: 'Fjalla One'},
    {value: 'Heebo', text: 'Heebo'},
    {value: 'Hind', text: 'Hind'},
    {value: 'Hind Madurai', text: 'Hind Madurai'},
    {value: 'Hind Siliguri', text: 'Hind Siliguri'},
    {value: 'IBM Plex Mono', text: 'IBM Plex Mono'},
    {value: 'IBM Plex Sans', text: 'IBM Plex Sans'},
    {value: 'Inconsolata', text: 'Inconsolata'},
    {value: 'Inter', text: 'Inter'},
    {value: 'Josefin Sans', text: 'Josefin Sans'},
    {value: 'Jost', text: 'Jost'},
    {value: 'Kanit', text: 'Kanit'},
    {value: 'Karla', text: 'Karla'},
    {value: 'Lato', text: 'Lato'},
    {value: 'Libre Baskerville', text: 'Libre Baskerville'},
    {value: 'Libre Franklin', text: 'Libre Franklin'},
    {value: 'Lobster', text: 'Lobster'},
    {value: 'Lora', text: 'Lora'},
    {value: 'M PLUS Rounded 1c', text: 'M PLUS Rounded 1c'},
    {value: 'Manrope', text: 'Manrope'},
    {value: 'Material Icons', text: 'Material Icons'},
    {value: 'Material Icons Outlined', text: 'Material Icons Outlined'},
    {value: 'Material Icons Round', text: 'Material Icons Round'},
    {value: 'Material Icons Sharp', text: 'Material Icons Sharp'},
    {value: 'Material Symbols Outlined', text: 'Material Symbols Outlined'},
    {value: 'Material Symbols Rounded', text: 'Material Symbols Rounded'},
    {value: 'Maven Pro', text: 'Maven Pro'},
    {value: 'Merriweather', text: 'Merriweather'},
    {value: 'Montserrat', text: 'Montserrat'},
    {value: 'Mukta', text: 'Mukta'},
    {value: 'Mulish', text: 'Mulish'},
    {value: 'Nanum Gothic', text: 'Nanum Gothic'},
    {value: 'Nanum Gothic Coding', text: 'Nanum Gothic Coding'},
    {value: 'Noto Color Emoji', text: 'Noto Color Emoji'},
    {value: 'Noto Sans', text: 'Noto Sans'},
    {value: 'Noto Serif', text: 'Noto Serif'},
    {value: 'Nunito', text: 'Nunito'},
    {value: 'Nunito Sans', text: 'Nunito Sans'},
    {value: 'Open Sans', text: 'Open Sans'},
    {value: 'Oswald', text: 'Oswald'},
    {value: 'Outfit', text: 'Outfit'},
    {value: 'Overpass', text: 'Overpass'},
    {value: 'Orbitron', text: 'Orbitron'},
    {value: 'Oxygen', text: 'Oxygen'},
    {value: 'Pacifico', text: 'Pacifico'},
    {value: 'Play', text: 'Play'},
    {value: 'Playfair Display', text: 'Playfair Display'},
    {value: 'Poppins', text: 'Poppins'},
    {value: 'Prompt', text: 'Prompt'},
    {value: 'PT Sans', text: 'PT Sans'},
    {value: 'PT Sans Narrow', text: 'PT Sans Narrow'},
    {value: 'PT Serif', text: 'PT Serif'},
    {value: 'Public Sans', text: 'Public Sans'},
    {value: 'Quicksand', text: 'Quicksand'},
    {value: 'Rajdhani', text: 'Rajdhani'},
    {value: 'Raleway', text: 'Raleway'},
    {value: 'Red Hat Display', text: 'Red Hat Display'},
    {value: 'Roboto', text: 'Roboto'},
    {value: 'Roboto Condensed', text: 'Roboto Condensed'},
    {value: 'Roboto Mono', text: 'Roboto Mono'},
    {value: 'Roboto Slab', text: 'Roboto Slab'},
    {value: 'Rubik', text: 'Rubik'},
    {value: 'Signika Negative', text: 'Signika Negative'},
    {value: 'Slabo 27px', text: 'Slabo 27px'},
    {value: 'Source Code Pro', text: 'Source Code Pro'},
    {value: 'Source Sans 3', text: 'Source Sans 3'},
    {value: 'Space Grotesk', text: 'Space Grotesk'},
    {value: 'Teko', text: 'Teko'},
    {value: 'Titillium Web', text: 'Titillium Web'},
    {value: 'Ubuntu', text: 'Ubuntu'},
    {value: 'Varela Round', text: 'Varela Round'},
    {value: 'Work Sans', text: 'Work Sans'},
];

const SUBSCRIPTION_PLANS = [
    {value: 'basic', text: 'Space Station'},
    {value: 'middle', text: 'Voyager'},
    {value: 'advanced', text: 'Luna'},
];

export const blogModule = {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
    TIME_ZONES,
    MESSAGE_BASIC_PLAN_RESTRICTION,
    CURRENCIES,
    FONTS,
    SUBSCRIPTION_PLANS,
}
